/**
 * RC7 · M9 · Emergency Playbooks — traveller-facing scripts.
 *
 * Bundled with the JS payload → available offline by definition (no
 * network fetch, no server call). Each playbook is intentionally short
 * and follows the PO-mandated structure:
 *
 *   WHAT TO DO NOW      → the first two actions, in order
 *   WHO TO CONTACT      → resolved from Safety Memory / cruise contacts
 *   WHAT INFO YOU NEED  → what to have ready when you make the call
 *   WHAT DOCUMENT       → deep-link into the Documents tab (M6)
 *   WHAT TO DO NEXT     → the follow-through
 *
 * Do NOT copy-paste medical or legal advice. Playbooks route the
 * traveller to the RIGHT PERSON as quickly as possible — nothing more.
 *
 * ⚠️ These are intentionally generic. Cruise-line-specific overrides
 * arrive via the backend `/api/mobile/safety/hub` payload where the
 * cruise-line safety contacts are already governed.
 */

export const EMERGENCY_PLAYBOOKS = Object.freeze([
  {
    id: "medical_emergency",
    title: "Medical emergency",
    severity: "critical",
    what_now: [
      "If you're onboard: dial the ship medical centre from any cabin phone or house phone.",
      "If you're ashore: call the local emergency number for the country you're in.",
    ],
    who_to_contact: ["ship_medical_centre", "emergency_contact", "primary_physician", "insurance_emergency_assistance"],
    info_needed: [
      "Your cabin number (onboard) or the exact address / port terminal you're at (ashore).",
      "Any allergies, medications, and existing conditions (kept in your Safety Memory).",
      "Blood type (from Safety Memory).",
    ],
    open_documents: ["medical", "insurance"],
    what_next: [
      "Ask the medical team for a written summary of care.",
      "Keep receipts — your insurer will need them.",
    ],
  },
  {
    id: "lost_passport",
    title: "Lost or stolen passport",
    severity: "urgent",
    what_now: [
      "File a police report immediately if the passport was stolen.",
      "Contact your nearest embassy or consulate to start an emergency travel document.",
    ],
    who_to_contact: ["consulate", "insurance_emergency_assistance", "ship_guest_services"],
    info_needed: [
      "The masked passport identifier and country of issue (Documents tab).",
      "Photo of the passport information page if you have one saved.",
      "A copy of your travel itinerary.",
    ],
    open_documents: ["passport"],
    what_next: [
      "Notify the cruise line's Guest Services so you can re-board and re-clear immigration.",
      "Contact your travel insurance provider — most cover emergency passport replacement.",
    ],
  },
  {
    id: "lost_or_stolen_documents",
    title: "Lost or stolen travel documents",
    severity: "urgent",
    what_now: [
      "File a police report.",
      "Notify your bank / card issuer if payment cards were affected.",
    ],
    who_to_contact: ["consulate", "insurance_emergency_assistance", "ship_guest_services"],
    info_needed: [
      "Booking confirmation numbers.",
      "Credit-card issuer emergency line (on the back of the card / your bank app).",
    ],
    open_documents: ["passport", "boarding_pass", "insurance"],
    what_next: [
      "Ask your cruise line for reprinted onboard credentials.",
      "Keep every incident report — insurance will require them.",
    ],
  },
  {
    id: "missed_ship",
    title: "Missed ship / missed port departure",
    severity: "urgent",
    what_now: [
      "Go to the cruise-line port agent's office — most ports have one at or near the terminal.",
      "Call the cruise-line 24-hour guest emergency line.",
    ],
    who_to_contact: ["cruise_line_emergency", "port_agent", "insurance_emergency_assistance"],
    info_needed: [
      "Your booking reference and cabin number.",
      "Your onward itinerary (dates + next port).",
      "Passport and credit card.",
    ],
    open_documents: ["boarding_pass", "passport"],
    what_next: [
      "The cruise line can typically arrange rejoining at the next port.",
      "Keep receipts for transportation — insurance may reimburse.",
    ],
  },
  {
    id: "emergency_onboard",
    title: "Emergency onboard",
    severity: "critical",
    what_now: [
      "Follow crew instructions immediately — go to your muster station if the alarm sounds.",
      "Use the ship's internal emergency line (posted in every cabin) for a private incident.",
    ],
    who_to_contact: ["ship_bridge", "ship_security", "ship_medical_centre"],
    info_needed: [
      "Your cabin number.",
      "The nature of the incident.",
    ],
    open_documents: [],
    what_next: [
      "Keep your keycard and phone on you at all times during a shipwide event.",
    ],
  },
  {
    id: "emergency_ashore",
    title: "Emergency ashore",
    severity: "critical",
    what_now: [
      "Call the local emergency number for the country you're in.",
      "Return to the ship or the local cruise-line port agent as soon as it is safe.",
    ],
    who_to_contact: ["local_emergency", "port_agent", "consulate"],
    info_needed: [
      "Your exact location (a photo of the nearest cross-street helps).",
      "Passport information (masked — from Documents).",
    ],
    open_documents: ["passport"],
    what_next: [
      "Notify the ship as soon as possible if you'll miss the all-aboard time.",
    ],
  },
  {
    id: "insurance_assistance",
    title: "Insurance / emergency assistance",
    severity: "advisory",
    what_now: [
      "Call your insurer's 24-hour emergency assistance line — the number is on your policy.",
    ],
    who_to_contact: ["insurance_emergency_assistance"],
    info_needed: [
      "Your policy number.",
      "A description of what happened.",
      "Any receipts or reports you already have.",
    ],
    open_documents: ["insurance"],
    what_next: [
      "Keep every receipt and written summary of care.",
    ],
  },
  {
    id: "consular_assistance",
    title: "Consular assistance",
    severity: "advisory",
    what_now: [
      "Contact your nearest embassy or consulate.",
      "If arrested or detained, insist on consular notification.",
    ],
    who_to_contact: ["consulate"],
    info_needed: [
      "Your passport (or a photo of the information page).",
      "Your onward itinerary.",
    ],
    open_documents: ["passport"],
    what_next: [
      "Follow written consular guidance — do not sign statements you don't understand.",
    ],
  },
]);

export function playbookById(id) {
  return EMERGENCY_PLAYBOOKS.find((p) => p.id === id) || null;
}
